'use client';
import { useState, useEffect } from 'react';

interface TechCoaching {
  techName: string;
  stars: number;
  strengths: string[];
  improvements: string[];
}

interface CoachingData {
  overallGrade: string;
  messagesAnalyzed: number;
  strengths: string[];
  weaknesses: string[];
  coachingTip: string;
  techBreakdown: TechCoaching[];
}

interface CoachingReportProps {
  date: string;
  branch: string;
}

function gradeColor(g: string): string {
  if (g.startsWith('A')) return 'text-green-600 bg-green-50 border-green-200';
  if (g.startsWith('B')) return 'text-blue-600 bg-blue-50 border-blue-200';
  if (g.startsWith('C')) return 'text-orange-600 bg-orange-50 border-orange-200';
  return 'text-red-600 bg-red-50 border-red-200';
}

export default function CoachingReport({ date, branch }: CoachingReportProps) {
  const [data, setData] = useState<CoachingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedTech, setExpandedTech] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ date });
    if (branch !== 'All') params.set('branch', branch);
    fetch(`/api/dashboard/coaching?${params}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setData(d && d.overallGrade ? d : null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [date, branch]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <div className="h-5 bg-gray-200 rounded w-48 mb-4 animate-pulse" />
        <div className="h-32 bg-gray-100 rounded animate-pulse" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100">
        <h2 className="font-semibold text-gray-800">🤖 AI Coaching Report</h2>
      </div>
      {!data ? (
        <div className="p-8 text-center">
          <div className="text-4xl mb-3">🤖</div>
          <div className="text-gray-500">Report will be generated at end of day</div>
          <div className="text-xs text-gray-400 mt-1">Check back after services are completed</div>
        </div>
      ) : (
        <div className="p-5 space-y-5">
          {/* Grade + Messages */}
          <div className="flex items-center gap-4">
            <div className={`text-4xl font-bold w-16 h-16 rounded-xl flex items-center justify-center border ${gradeColor(data.overallGrade)}`}>
              {data.overallGrade}
            </div>
            <div>
              <div className="text-sm font-medium text-gray-700">Overall Team Grade</div>
              <div className="text-xs text-gray-500">Messages Analyzed: {data.messagesAnalyzed}</div>
            </div>
          </div>

          {/* Patterns */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="text-xs font-semibold text-green-600 uppercase mb-2">Strengths</div>
              {(data.strengths || []).map((s, i) => (
                <div key={i} className="text-sm text-gray-700 flex items-start gap-1.5 mb-1">
                  <span className="text-green-500 mt-0.5">✓</span> {s}
                </div>
              ))}
            </div>
            <div>
              <div className="text-xs font-semibold text-orange-500 uppercase mb-2">Areas to Improve</div>
              {(data.weaknesses || []).map((w, i) => (
                <div key={i} className="text-sm text-gray-700 flex items-start gap-1.5 mb-1">
                  <span className="text-orange-400 mt-0.5">!</span> {w}
                </div>
              ))}
            </div>
          </div>

          {/* Coaching Tip */}
          <div className="bg-[#01AA90]/10 border border-[#01AA90]/30 rounded-lg p-4">
            <div className="text-xs font-semibold text-[#01AA90] uppercase mb-1">💡 Coaching Tip of the Day</div>
            <div className="text-sm text-gray-700">{data.coachingTip || 'No tip generated for this day.'}</div>
          </div>

          {/* Tech Breakdown */}
          {data.techBreakdown?.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Per-Tech Breakdown</div>
              <div className="space-y-1">
                {(data.techBreakdown || []).map(tech => (
                  <div key={tech.techName} className="border border-gray-100 rounded-lg">
                    <button
                      className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 text-left"
                      onClick={() => setExpandedTech(expandedTech === tech.techName ? null : tech.techName)}
                    >
                      <span className="text-sm font-medium text-gray-700">{tech.techName}</span>
                      <span className="text-sm text-yellow-500">{'★'.repeat(Math.max(0, Math.min(5, tech.stars || 0)))}{'☆'.repeat(5 - Math.max(0, Math.min(5, tech.stars || 0)))}</span>
                    </button>
                    {expandedTech === tech.techName && (
                      <div className="px-4 pb-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                        <div>
                          {(tech.strengths || []).map((s, i) => (
                            <div key={i} className="text-green-700 mb-0.5">✓ {s}</div>
                          ))}
                        </div>
                        <div>
                          {(tech.improvements || []).map((s, i) => (
                            <div key={i} className="text-orange-600 mb-0.5">→ {s}</div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
